import {ActivatedRoute, Router} from '@angular/router';
import {AppUpdateService} from '../app-update.service';
import {AuthenticationService} from '../authentication.service';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TimezonesService} from '../timezones.service';
import {TranslateService} from '@ngx-translate/core';
import {first} from 'rxjs/operators';
import {Observable, of, Subscription} from 'rxjs';
import {isNil} from 'lodash-es';
import {marker} from '@biesbjerg/ngx-translate-extract-marker';
import {SwitchLanguageService} from "../switch-language/switch-language.service";


@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  loading = false;
  submitted = false;
  returnUrl: string;
  error: Observable<string>;
  loginSuccessMessage: Observable<string>;
  sub: Subscription;
  orgSelectionVisible = false; // shows/hides select box for setting the default organization
  private queryParamsReturnUrl: string;
  passwordReset = false;
  loginSuccessful = false;

  constructor(
    public authenticationService: AuthenticationService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private timezonesService: TimezonesService,
    private appUpdateService: AppUpdateService,
    private switchLanguageService: SwitchLanguageService,
  ) {
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  ngOnInit() {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      selectOrganization: [],
    });

    // https://medium.com/@christo8989/angular-6-url-parameters-860db789db85
    this.route.queryParamMap.subscribe(queryParams => {
      this.queryParamsReturnUrl = queryParams.get('returnUrl');
    });
    // to handle the situations when a new page is loaded in a new tab or login is accessed manually
    if (!isNil(this.returnUrl) && Boolean(localStorage.getItem('isLoggedIn'))) {
      this.router.navigate([this.returnUrl]);
    }

    this.authenticationService.logoutEvent.next(false);

    this.initializeLanguage();
  }

  private initializeLanguage() {
    const browserLanguageCode = typeof navigator !== 'undefined'
      ? navigator.language?.slice(0, 2)
      : 'en';
    const browserLanguage = this.switchLanguageService.toLanguage(browserLanguageCode);
    let userSavedLanguage = this.switchLanguageService.getSavedLanguageCode();
    if (userSavedLanguage == null) {
      this.switchLanguageService.switchLanguage(browserLanguage);
    }
  }

  isFieldInvalid(field: string) { // {6}
    return (
      (!this.loginForm.get(field).valid && this.loginForm.get(field).touched) ||
      (this.loginForm.get(field).untouched && this.submitted)
    );
  }

  login() {
    this.loginSuccessful = true;
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    if (this.sub !== undefined) {
      this.sub.unsubscribe();
    }

    this.loading = true;
    this.timezonesService.getTimezones();

    this.sub = this.authenticationService.login(this.f.username.value, this.f.password.value)
      .pipe(
        first(),
      )
      .subscribe(
        data => {
          this.disableLoginFields();
          this.loading = false;
          this.error = of(''); // reset any previous error message
          this.passwordReset = data.passwordReset;

          this.loginSuccessMessage = this.translateService.stream(marker('Success!'));

          this.orgSelectionVisible = !this.authenticationService.organizationChangeDisabled;

          this.returnUrl = this.computeReturnURL(this.queryParamsReturnUrl, data);

          if (this.authenticationService.organizationChangeDisabled) {
            this.setLoggedIn();
            this.navigateTo(this.returnUrl);
          }

          if (this.appUpdateService.isUpdatesEnabled) {
            this.appUpdateService.checkForUpdate();
          }

        },
        error => {
          this.loginSuccessful = false;
          if (error?.error?.accountLocked) {
            this.error = this.translateService.stream(marker('Oops! Your account has been locked due to too many failed password attempts. Please use the Forgot Password screen to reset your Password.')); // error.error.errorMessage;
          }
          else if(error.status == 0) {
            this.error = this.translateService.stream((marker('Unable to connect to server')));
          }
          else {
            this.error = this.translateService.stream(marker('Invalid username or password')); // error.error.errorMessage;
          }
          this.loading = false;
        });
  }

  private computeReturnURL(queryParamsReturnURL, userProfile) {
    if (userProfile.passwordReset) {
      return '/force-change-password';
    }

    if (userProfile.isVibaUser && !userProfile.isVibaAdmin) {
      return '/viba/agent/agent-live';
    }

    return queryParamsReturnURL || '/home';
  }

  private disableLoginFields(): void {
    this.loginForm.get('username').disable();
    this.loginForm.get('password').disable();
  }

  setLoggedIn() {
    this.authenticationService.isLoggedIn = true; // so we can see the navigation menu only atfer the user choses an organization
    localStorage.setItem('isLoggedIn', String(this.authenticationService.isLoggedIn));
  }

  onOrganizationChanged(org) {
    this.setLoggedIn();
    this.authenticationService.setActiveOrganization(org.value).subscribe(() => {
      if (isNil(this.returnUrl)) {
        this.returnUrl = this.queryParamsReturnUrl || '/home';
      }
      if (this.passwordReset) {
        this.returnUrl = '/force-change-password';
        this.navigateTo(this.returnUrl);
        return;
      }
      this.navigateTo(this.returnUrl);
    });
  }

  private navigateTo(url: string): void {
    this.router.navigate([this.returnUrl]).then(
      suceeded => {
        if (!suceeded) {
          this.router.navigate(['/home']);
        }
      },
    );
  }

  ngOnDestroy(): void {
    if (this.sub !== undefined) {
      this.sub.unsubscribe();
    }
  }

}
